import React, { FC, useState, useEffect, useRef } from 'react';

import { StyledArchiveGrid } from './styled/StyledArchiveGrid';
import { StyledSearchBanner } from './styled/StyledSearchBanner';
import { BlogCard } from '@components/Molecules/BlogCard';
import { Button } from '@components/Atoms';
import { ButtonType } from '@components/Atoms/Button/types';
import { Category } from '@components/Atoms/Category';
import { SearchBar } from '@components/Molecules';

interface Props {
  theme?: string;
  blogs: Array<{
    id: number;
    blog_title: string;
    admin_user: {
      id: number;
      firstname: string;
      lastname: string;
      username: string;
      preferedLanguage: string;
    };
    published_at: string;
    created_at: string;
    updated_at: string;
    categories: Array<{
      id: number;
      category_name: string;
    }>;
    featured_image: {
      id: number;
      name: string;
      alternativeText: string;
      caption: string;
      width: number;
      height: number;
      formats: {
        large: {
          ext: string;
          url: string;
          hash: string;
          mime: string;
          name: string;
          path: string;
          size: number;
          width: number;
          height: number;
        };
        small: {
          ext: string;
          url: string;
          hash: string;
          mime: string;
          name: string;
          path: string;
          size: number;
          width: number;
          height: number;
        };
        medium: {
          ext: string;
          url: string;
          hash: string;
          mime: string;
          name: string;
          path: string;
          size: number;
          width: number;
          height: number;
        };
        thumbnail: {
          ext: string;
          url: string;
          hash: string;
          mime: string;
          name: string;
          path: string;
          size: number;
          width: number;
          height: number;
        };
      };
      hash: string;
      ext: string;
      mime: string;
      size: number;
      url: string;
      previewUrl: string;
      provider: string;
      provider_metadata: string;
      created_at: string;
      updated_at: string;
    };
    tags: Array<{
      id: number;
      tag_name: string;
      published_at: string;
      created_at: string;
      updated_at: string;
      blog: number;
    }>;
    slug: string;
    excerpt: string;
    author: {
      author_name: string;
    };
    blog_content: Array<{
      type: string;
      text: string;
    }>;
    postType?: string;
  }>;

  categories?: Array<{
    id: number;
    category_name: string;
  }>;

  title: string;
  postType?: string;
}

export const ArchiveGrid: FC<Props> = ({
  blogs = [],
  categories,
  title,
  theme = 'default',
  postType = 'blog',
}) => {
  const [numToShow, setNumToShow] = useState(6);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const [filteredData, setFilteredData] = useState(blogs);

  const handleLoadMore = () => {
    setNumToShow(numToShow + 6);
  };

  const handleCategoryClick = (category) => {
    if (category === selectedCategory) {
      setSelectedCategory(null);
      setFilteredData(blogs);
      setNumToShow(6);
    } else {
      setSelectedCategory(category);
      setNumToShow(6);
      setFilteredData(
        blogs.filter((blog) => {
          return blog.categories.some((cat) => cat.id === category);
        }),
      );
    }
  };

  // Search blogs by title, category, excerpt, tags, author, textElement blocks
  const handleSearch = (query) => {
    setSelectedCategory(null);
    query = query.trim().toLowerCase();
    const filtered = blogs.filter((blog) => {
      return (
        blog.blog_title.toLowerCase().includes(query.toLowerCase()) ||
        blog.categories.some((category) =>
          category.category_name.toLowerCase().includes(query.toLowerCase()),
        ) ||
        blog.excerpt.toLowerCase().includes(query.toLowerCase()) ||
        blog.tags.some((tag) => tag.tag_name.toLowerCase().includes(query)) ||
        blog.author.author_name.toLowerCase().includes(query.toLowerCase()) ||
        blog.blog_content.some(
          (content) =>
            content.type === 'textElement' &&
            content.text.toLowerCase().includes(query.toLowerCase()),
        )
      );
    });
    setFilteredData(filtered);
  };

  return (
    <StyledArchiveGrid id="blogs_archive" gridTheme={theme}>
      {categories && categories.length > 0 && (
        <StyledSearchBanner>
          <div className="search-banner">
            <div className="container search-banner__content">
              <div className="search-banner__categories">
                <p>Categories</p>
                <div className="search-banner__categories__cats">
                  {categories.map((category) => (
                    <button
                      className={`search-banner__category ${
                        category.id === selectedCategory ? 'active' : ''
                      }`}
                      key={category.id}
                      onClick={() => handleCategoryClick(category.id)}
                    >
                      <Category text={category.category_name} />
                    </button>
                  ))}
                </div>
              </div>
              <div className="search-banner__search">
                <SearchBar onSearch={handleSearch} />
              </div>
            </div>
          </div>
        </StyledSearchBanner>
      )}
      <div className="container pt-10 pb-10 mx-auto archive-grid lg:pt-20 lg:my-20">
        <div className="archive-grid__container">
          <h3 className="archive-grid__title">{title}</h3>
          <div className="archive-grid__grid">
            {filteredData.length === 0 ? (
              <p className="no-results">No search results</p>
            ) : (
              filteredData.slice(0, numToShow).map((item) => (
                <div className="archive-grid__item fade-in" key={item.id}>
                  <BlogCard
                    {...item}
                    postType={postType == 'combined' ? item.postType : postType}
                  />
                </div>
              ))
            )}
          </div>
          {numToShow < filteredData.length && (
            <Button
              as="button"
              buttonType={ButtonType.PRIMARY}
              onClick={handleLoadMore}
              className="m-auto"
            >
              Load More
            </Button>
          )}
        </div>
      </div>
    </StyledArchiveGrid>
  );
};
